import { useState } from 'react';
import { api, ApiError, formatDate, newIdempotencyKey } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M11_ROLES, hasAnyRole } from '../../lib/roles';
import type { Page, SupplierProfile } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const TABS = [
  { key: 'mine', label: 'My supplier profile' },
  { key: 'all', label: 'All suppliers (staff)' },
] as const;

export function Suppliers() {
  const { persona } = useAuth();
  const canManage = hasAnyRole(persona?.roles, M11_ROLES.PROCUREMENT_OFFICER, M11_ROLES.TENDER_BOARD, M11_ROLES.NPTB_OVERSIGHT);
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>(canManage ? 'all' : 'mine');

  return (
    <div>
      <PageHeader title="Suppliers" subtitle="Registration, Sey-X attestation, and standing across the procurement register." />
      <Tabs tabs={canManage ? TABS : TABS.filter((t) => t.key === 'mine')} value={tab} onChange={setTab} />
      {tab === 'mine' ? <MyProfile /> : <AdminList />}
    </div>
  );
}

function MyProfile() {
  const { persona } = useAuth();
  const actingFor = persona?.actingForBusinessId;
  const [showForm, setShowForm] = useState(false);
  // Hooks called unconditionally — see BidTab.tsx's VendorBidPanel for why.
  const mineQ = useApiQuery<{ items: SupplierProfile[] }>(actingFor ? '/api/v1/m11/suppliers/me' : null, actingFor);

  if (!persona) return <InfoNote message="Sign in as a business representative to register a supplier profile — see the sidebar's Sign in link." />;
  if (!actingFor) {
    return <InfoNote message="Supplier profiles always belong to a business (X-Acting-For mandate) — M11 has no individual-supplier path. Sign in as a business representative persona." />;
  }

  return (
    <div className="space-y-4">
      {mineQ.error ? <ErrorNote message={mineQ.error} /> : null}
      {mineQ.loading ? (
        <Spinner />
      ) : (mineQ.data?.items.length ?? 0) === 0 && !showForm ? (
        <div className="card p-6 text-center">
          <p className="mb-3 text-sm text-slate-600">No supplier profile yet for {actingFor}.</p>
          <button className="btn-primary" onClick={() => setShowForm(true)}>Register as a supplier</button>
        </div>
      ) : (
        mineQ.data?.items.map((s) => <SupplierCard key={s.id} supplier={s} />)
      )}
      {showForm ? <RegisterForm actingFor={actingFor} onDone={() => { setShowForm(false); mineQ.reload(); }} onCancel={() => setShowForm(false)} /> : null}
    </div>
  );
}

function SupplierCard({ supplier }: { supplier: SupplierProfile }) {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-slate-900">{supplier.legalName}{supplier.tradingName ? ` (t/a ${supplier.tradingName})` : ''}</div>
          <div className="text-xs text-slate-500">{supplier.refNo} · {supplier.contactEmail}</div>
        </div>
        <StatusBadge status={supplier.status} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
        <div><div className="font-semibold uppercase text-slate-400">Tax clearance</div><StatusBadge status={supplier.taxClearance.status} /></div>
        <div><div className="font-semibold uppercase text-slate-400">Licence check</div><StatusBadge status={supplier.licence.status} /></div>
        <div><div className="font-semibold uppercase text-slate-400">Blacklist</div><StatusBadge status={supplier.blacklist.status} /></div>
      </div>
      <div className="mt-3 text-xs text-slate-500">Categories: {supplier.categories.join(', ')}</div>
      <div className="mt-1 text-xs text-slate-500">Performance score: <span className="font-semibold tabular-nums">{supplier.performanceScore}</span></div>
      {supplier.rejectionReason ? <div className="mt-2"><ErrorNote message={supplier.rejectionReason} /></div> : null}
    </div>
  );
}

function RegisterForm({ actingFor, onDone, onCancel }: { actingFor: string; onDone: () => void; onCancel: () => void }) {
  const [legalName, setLegalName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [categories, setCategories] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(
        '/api/v1/m11/suppliers',
        {
          legalName,
          tradingName: tradingName || undefined,
          contactEmail,
          contactPhone: contactPhone || undefined,
          categories: categories.split(',').map((c) => c.trim()).filter(Boolean),
        },
        { actingFor, idempotencyKey: newIdempotencyKey() },
      );
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Registration failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Register {actingFor} as a supplier</h2>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Legal name"><input className="field-input" value={legalName} onChange={(e) => setLegalName(e.target.value)} /></Field>
          <Field label="Trading name (optional)"><input className="field-input" value={tradingName} onChange={(e) => setTradingName(e.target.value)} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact email"><input className="field-input" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} /></Field>
          <Field label="Contact phone (optional)"><input className="field-input" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} /></Field>
        </div>
        <Field label="Procurement categories (comma separated)" hint="e.g. CONSTRUCTION, IT, MEDICAL_SUPPLIES">
          <input className="field-input" value={categories} onChange={(e) => setCategories(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={busy || legalName.length < 2 || !contactEmail || categories.split(',').filter((c) => c.trim()).length === 0}>
            {busy ? 'Registering…' : 'Register'}
          </button>
        </div>
        <p className="text-[11px] text-slate-400">Tax clearance and licence checks run automatically (Sey-X) — if both PASS the profile is instantly VERIFIED, otherwise it waits on officer attestation.</p>
      </div>
    </div>
  );
}

function AdminList() {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const query = new URLSearchParams({ limit: '50' });
  if (status) query.set('status', status);
  if (q) query.set('q', q);
  const listQ = useApiQuery<Page<SupplierProfile>>(`/api/v1/m11/suppliers?${query.toString()}`);

  return (
    <div>
      <div className="mb-4 grid gap-2 sm:grid-cols-2">
        <Field label="Status" className="!mb-0">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="PENDING">Pending</option>
            <option value="VERIFIED">Verified</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </Field>
        <Field label="Search" className="!mb-0"><input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Legal name, ref, entity id…" /></Field>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      {listQ.loading ? (
        <Spinner />
      ) : (listQ.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="No suppliers match" />
      ) : (
        <div className="space-y-3">
          {listQ.data!.items.map((s) => <AdminRow key={s.id} supplier={s} onChanged={listQ.reload} />)}
        </div>
      )}
    </div>
  );
}

function AdminRow({ supplier, onChanged }: { supplier: SupplierProfile; onChanged: () => void }) {
  const { persona } = useAuth();
  const canAttest = hasAnyRole(persona?.roles, M11_ROLES.PROCUREMENT_OFFICER, M11_ROLES.NPTB_OVERSIGHT);
  const canOversight = hasAnyRole(persona?.roles, M11_ROLES.NPTB_OVERSIGHT);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [showReasonFor, setShowReasonFor] = useState<'reject' | 'blacklist' | null>(null);

  const act = async (path: string, body?: unknown) => {
    setBusy(path);
    setError(null);
    try {
      await api.post(`/api/v1/m11/suppliers/${supplier.id}/${path}`, body);
      setShowReasonFor(null);
      setReason('');
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : `${path} failed`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-bold">{supplier.legalName}</div>
          <div className="text-xs text-slate-500">{supplier.refNo} · entity {supplier.entityId}</div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={supplier.status} />
          <StatusBadge status={supplier.blacklist.status} />
        </div>
      </div>
      {error ? <div className="mt-2"><ErrorNote message={error} /></div> : null}
      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <span>Tax: <StatusBadge status={supplier.taxClearance.status} /></span>
        <span>Licence: <StatusBadge status={supplier.licence.status} /></span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {canAttest && supplier.status === 'PENDING' ? (
          <>
            <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => act('attest-tax', { status: 'PASS' })} disabled={busy !== null}>Attest tax PASS</button>
            <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => act('attest-licence', { status: 'PASS' })} disabled={busy !== null}>Attest licence PASS</button>
            <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReasonFor('reject')} disabled={busy !== null}>Reject</button>
          </>
        ) : null}
        {canOversight && supplier.blacklist.status === 'NONE' ? (
          <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReasonFor('blacklist')} disabled={busy !== null}>Blacklist</button>
        ) : null}
        {canOversight && supplier.blacklist.status !== 'NONE' ? (
          <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => act('reinstate', {})} disabled={busy !== null}>Reinstate</button>
        ) : null}
      </div>
      {showReasonFor ? (
        <div className="mt-2 flex gap-2">
          <input className="field-input" placeholder="Reason…" value={reason} onChange={(e) => setReason(e.target.value)} />
          <button className="btn-danger shrink-0 !py-1 text-xs" onClick={() => act(showReasonFor, { reason })} disabled={reason.length < 3 || busy !== null}>Confirm</button>
          <button className="btn-secondary shrink-0 !py-1 text-xs" onClick={() => setShowReasonFor(null)}>Cancel</button>
        </div>
      ) : null}
      <div className="mt-2 text-[11px] text-slate-400">Registered {formatDate(supplier.createdAt)}</div>
    </div>
  );
}
